import moment from 'moment';
import {
  getShipcluesBadgePresentation,
  humanizeShipcluesCode,
  normalizeShipcluesTrackingCode,
} from './shipclues_tracking_codes';

/** Mapped shipment statuses from backend PR #73 */
export const SHIPMENT_STATUSES = [
  'Packed',
  'Shipped',
  'Out for Delivery',
  'Delivered',
  'Returned',
  'Cancelled',
];

export const SHIPMENT_STEP_LABELS = [
  'Packed',
  'Shipped',
  'Out for Delivery',
  'Delivered',
];

const IN_TRANSIT_STATUSES = ['Packed', 'Shipped', 'Out for Delivery'];

const STATUS_BADGE_VARIANT = {
  Packed: 'secondary',
  Shipped: 'info',
  'Out for Delivery': 'primary',
  Delivered: 'success',
  Returned: 'warning',
  Cancelled: 'danger',
  'In transit': 'info',
  'Out for delivery': 'primary',
  RTO: 'warning',
};

export const getShipmentStatusBadgeVariant = (status) =>
  STATUS_BADGE_VARIANT[status] || 'secondary';

const firstNonEmptyArray = (candidates) => {
  const found = candidates.find((c) => Array.isArray(c) && c.length > 0);
  return found || [];
};

export const pickTrackingHistoryArray = (order) =>
  firstNonEmptyArray([
    order?.trackingHistory,
    order?.tracking?.history,
    order?.tracking?.trackingHistory,
    order?.shipment?.trackingHistory,
    order?.shipment?.tracking?.history,
    Array.isArray(order?.tracking) ? order.tracking : null,
    Array.isArray(order?.shipment?.tracking) ? order.shipment.tracking : null,
  ]);

export const normalizeTrackingHistory = (order) => {
  const list = pickTrackingHistoryArray(order);
  return [...list].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

export const getOrderAwb = (order) => {
  const candidates = [
    order?.awb,
    order?.awbNumber,
    order?.shipment?.awb,
    order?.shipment?.awbNumber,
    order?.tracking?.awb,
    order?.tracking?.awbNumber,
  ];
  const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
  return found != null ? String(found).trim() : null;
};

export const getShipmentStatus = (order) => {
  const candidates = [
    order?.shipmentStatus,
    order?.shipment?.status,
    order?.shipment?.shipmentStatus,
    order?.tracking?.status,
    order?.tracking?.shipmentStatus,
  ];
  const direct = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
  if (direct) return String(direct).trim();
  const latest = normalizeTrackingHistory(order)[0];
  return latest?.status ? String(latest.status).trim() : null;
};

export const getShipmentStatusRaw = (order) =>
  getLatestTrackingStatusCode(order) ||
  order?.shipmentStatusRaw ||
  order?.shipment?.statusRaw ||
  order?.tracking?.rawStatus ||
  normalizeTrackingHistory(order)[0]?.rawStatus ||
  null;

const pickOrderField = (order, keys) => {
  const sources = [order, order?.shipment, order?.tracking];
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
  }
  return null;
};

export const getLatestTrackingStatusCode = (order) => {
  const raw = pickOrderField(order, ['latestTrackingStatus', 'latest_tracking_status']);
  return raw ? normalizeShipcluesTrackingCode(raw) : null;
};

export const getLatestTrackingStatusLabel = (order) => {
  const label = pickOrderField(order, ['latestTrackingStatusLabel', 'latest_tracking_status_label']);
  return label ? String(label).trim() : null;
};

export const getShipmentLastLocation = (order) => {
  const loc = pickOrderField(order, ['shipmentLastLocation', 'shipment_last_location']);
  if (loc) return String(loc).trim();
  const latest = normalizeTrackingHistory(order)[0];
  return latest?.location ? String(latest.location).trim() : null;
};

export const getShipmentException = (order) => {
  const raw = pickOrderField(order, ['shipmentException', 'shipment_exception']);
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
};

export const getOrderCourierName = (order) => {
  const history = normalizeTrackingHistory(order);
  const fromEvent = history.find((e) => e?.courier)?.courier || history[0]?.courier;
  const candidates = [
    fromEvent,
    order?.courierName,
    order?.courier,
    order?.shipment?.courierName,
    order?.shipment?.courier,
    order?.tracking?.courierName,
    order?.tracking?.courier,
  ];
  const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
  return found != null ? String(found).trim() : 'Courier';
};

/** @deprecated use getOrderCourierName */
export const getShipmentCourier = getOrderCourierName;

export const getExpectedDeliveryDate = (order) => {
  const candidates = [
    order?.expectedDeliveryDate,
    order?.shipment?.expectedDeliveryDate,
    order?.tracking?.expectedDeliveryDate,
    order?.eta,
    order?.shipment?.eta,
  ];
  const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
  return found != null ? found : null;
};

export const formatTrackingTimestampIst = (timestamp) => {
  if (!timestamp) return '—';
  return moment(timestamp).utcOffset('+05:30').format('DD MMM YYYY, hh:mm A [IST]');
};

export const formatEtaDate = (date) => {
  if (!date) return null;
  return moment(date).utcOffset('+05:30').format('DD MMM YYYY');
};

export const formatAwbShort = (awb, maxLength = 14) => {
  const value = String(awb || '').trim();
  if (!value) return '';
  if (value.length <= maxLength) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
};

export const getListShipmentBadge = (order) => {
  const code = getLatestTrackingStatusCode(order);
  const apiLabel = getLatestTrackingStatusLabel(order);

  if (code || apiLabel) {
    const tone = getShipcluesBadgePresentation(code);
    return {
      label: apiLabel || humanizeShipcluesCode(code),
      code,
      bsVariant: tone.bsVariant,
      className: tone.className,
      variant: tone.bsVariant || 'custom',
    };
  }

  const status = getShipmentStatus(order);
  const latest = normalizeTrackingHistory(order)[0];
  const blob = `${status || ''} ${latest?.status || ''} ${latest?.rawStatus || ''}`.toLowerCase();

  let label = status || 'In transit';
  let bsVariant = 'info';
  let className = 'order-shipment-badge--transit';

  if (orderHasRto(order) || status === 'Returned') {
    label = 'RTO';
    bsVariant = null;
    className = 'order-shipment-badge--rto';
  } else if (status === 'Delivered' || blob.includes('delivered')) {
    label = 'Delivered';
    bsVariant = 'success';
    className = '';
  } else if (status === 'Out for Delivery' || blob.includes('out for delivery') || blob === 'ofd') {
    label = 'Out for delivery';
    bsVariant = 'warning';
    className = 'order-shipment-badge--ofd';
  }

  return { label, code: null, bsVariant, className, variant: bsVariant || 'custom' };
};

export const isNdrTrackingEvent = (event) => {
  const blob = [event?.status, event?.rawStatus, event?.remark]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
  return (
    blob.includes('NDR') ||
    blob.includes('NON DELIVERY') ||
    blob.includes('NOT DELIVERED') ||
    blob.includes('DELIVERY ATTEMPT')
  );
};

export const orderHasNdr = (order) => {
  const raw = String(getShipmentStatusRaw(order) || '').toUpperCase();
  if (raw.includes('NDR')) return true;
  return normalizeTrackingHistory(order).some(isNdrTrackingEvent);
};

export const orderHasRto = (order) => {
  const code = getLatestTrackingStatusCode(order);
  if (code && (code.startsWith('RTO_') || code === 'RETURNED_TO_SELLER')) return true;
  if (getShipmentStatus(order) === 'Returned') return true;
  const raw = String(getShipmentStatusRaw(order) || '').toUpperCase();
  return raw.includes('RTO') || raw.includes('RETURN');
};

export const isShipmentNotStarted = (order) => {
  const awb = getOrderAwb(order);
  const history = normalizeTrackingHistory(order);
  const status = getShipmentStatus(order);
  const trackingCode = getLatestTrackingStatusCode(order);
  const hasShipclues = order?.shipcluesOrderId || order?.shipcluesShipmentId;

  if (trackingCode && trackingCode !== 'PENDING') return false;
  if (history.length > 0 || awb) return false;

  if (!status && !hasShipclues) {
    return true;
  }
  if (status === 'Packed' && !awb && history.length === 0) {
    return true;
  }
  return false;
};

export const getLastShipmentUpdateTime = (order) => {
  const lastUpdate = pickOrderField(order, ['lastUpdate', 'last_update']);
  if (lastUpdate) return lastUpdate;
  const history = normalizeTrackingHistory(order);
  if (history[0]?.timestamp) return history[0].timestamp;
  if (order?.shipmentUpdatedAt) return order.shipmentUpdatedAt;
  if (order?.tracking?.lastUpdatedAt) return order.tracking.lastUpdatedAt;
  if (order?.updatedAt) return order.updatedAt;
  return null;
};

export const isShipmentStuck = (order, now = new Date()) => {
  const status = getShipmentStatus(order);
  if (!status || !IN_TRANSIT_STATUSES.includes(status)) return false;
  if (['Delivered', 'Returned', 'Cancelled'].includes(status)) return false;

  const last = getLastShipmentUpdateTime(order);
  if (!last) return false;

  const days = moment(now).diff(moment(last), 'days', true);
  return days >= 3;
};

export const getShipmentListWarnings = (order) => {
  const warnings = [];
  if (getShipmentException(order)) {
    warnings.push({ code: 'exception', label: 'Exception', tone: 'danger' });
  }
  if (orderHasNdr(order)) warnings.push({ code: 'ndr', label: 'NDR' });
  if (orderHasRto(order)) warnings.push({ code: 'rto', label: 'RTO' });
  if (isShipmentStuck(order)) warnings.push({ code: 'stuck', label: 'Stuck' });
  return warnings;
};

export const getShipmentStepperSteps = (order) => {
  const status = getShipmentStatus(order);
  const ndr = orderHasNdr(order);
  const rto = orderHasRto(order);
  const cancelled = status === 'Cancelled';
  const statusIndex = SHIPMENT_STEP_LABELS.indexOf(status);

  return SHIPMENT_STEP_LABELS.map((label, index) => {
    let state = 'upcoming';
    if (cancelled && index === SHIPMENT_STEP_LABELS.length - 1) {
      state = 'cancelled';
    } else if (rto && index >= 2) {
      state = 'exception';
    } else if (ndr && index === 2) {
      state = 'ndr';
    } else if (status === 'Delivered' || statusIndex > index) {
      state = 'complete';
    } else if (statusIndex === index) {
      state = 'current';
    } else if (status === 'Returned' && index >= 2) {
      state = 'exception';
    }

    return { label, state };
  });
};

export const getTimelineEventIcon = (event, order) => {
  if (isNdrTrackingEvent(event)) return 'ri-error-warning-line';
  const status = String(event?.status || '').toLowerCase();
  if (status.includes('deliver')) return 'ri-checkbox-circle-line';
  if (status.includes('out')) return 'ri-truck-line';
  if (status.includes('trip') || status.includes('arriv')) return 'ri-map-pin-line';
  if (status.includes('ship')) return 'ri-send-plane-line';
  if (status.includes('pack')) return 'ri-box-3-line';
  if (orderHasRto(order)) return 'ri-arrow-go-back-line';
  return 'ri-record-circle-line';
};

/** Realistic list/detail fixture (Delhivery / Shipclues shape). */
export const MOCK_DELHIVERY_LIST_ORDER = {
  _id: 'mock-delhivery-order',
  orderNumber: 'ORD-DEL-338085',
  shipmentStatus: 'Shipped',
  shipmentStatusRaw: 'TRIP_ARRIVED',
  latestTrackingStatus: 'IN_TRANSIT',
  latestTrackingStatusLabel: 'In Transit',
  shipmentException: false,
  lastUpdate: '2026-10-23T04:03:00.000Z',
  shipmentLastLocation: 'Agra_Jaupura_H (Uttar Pradesh)',
  awb: '33808519607695',
  courierName: 'Delhivery Exp./ Air',
  expectedDeliveryDate: '2026-10-27',
  trackingHistory: [
    {
      status: 'Trip Arrived',
      rawStatus: 'TRIP_ARRIVED',
      location: 'Agra_Jaupura_H (Uttar Pradesh)',
      remark: 'Shipment arrived at hub',
      timestamp: '2026-10-23T04:03:00.000Z',
      courier: 'Delhivery Exp./ Air',
    },
  ],
};

export const MOCK_SHIPMENT_ORDER_IN_TRANSIT = {
  _id: 'mock-order-1',
  orderNumber: 'CV-2401-8821',
  shipmentStatus: 'Out for Delivery',
  shipmentStatusRaw: 'OFD',
  awb: 'AWB782910334455',
  shipcluesOrderId: 'SC-ORD-9912',
  shipcluesShipmentId: 'SC-SHP-4412',
  trackingHistory: [
    {
      status: 'Out for Delivery',
      rawStatus: 'OFD',
      location: 'Mumbai Hub',
      remark: 'Shipment out for delivery',
      timestamp: '2025-10-20T09:15:00.000Z',
      courier: 'Delhivery',
    },
    {
      status: 'Shipped',
      rawStatus: 'IN_TRANSIT',
      location: 'Bhiwandi, MH',
      remark: 'Departed origin facility',
      timestamp: '2025-10-18T14:22:00.000Z',
      courier: 'Delhivery',
    },
    {
      status: 'Packed',
      rawStatus: 'PICKED_UP',
      location: 'Careveli Warehouse',
      remark: 'Order packed and handed to courier',
      timestamp: '2025-10-17T11:00:00.000Z',
      courier: 'Delhivery',
    },
  ],
};

export const MOCK_SHIPMENT_ORDER_NOT_SHIPPED = {
  _id: 'mock-order-2',
  orderNumber: 'CV-2401-9900',
  status: 'Processing',
};
